import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const BASE = 'Hippocrate MG';
const DEFAULT_DESC = "Hippocrate MG : l'assistant documentaire intelligent des médecins français. Recherche médicale sourcée (HAS, Ameli, Collèges), cotation CCAM officielle, scores cliniques validés et bibliothèque de référentiels indexée par IA.";

const META = {
  '/login': {
    title: `Connexion — ${BASE} | Assistant Documentaire du Médecin`,
    desc: "Connectez-vous à Hippocrate MG, l'assistant documentaire IA des médecins : recherche médicale sourcée, cotation CCAM, scores cliniques validés.",
  },
  '/dashboard': {
    title: `Tableau de Bord — ${BASE} | Tous vos outils de consultation`,
    desc: "Accédez à tous vos outils d'aide documentaire : fiche diagnostic, recherche ciblée, médicaments, cotation CCAM, scores médicaux et bibliothèque de référentiels.",
  },
  '/diagnosis': {
    title: `Fiche Diagnostic — Synthèse documentaire clinique | ${BASE}`,
    desc: "Générez une synthèse documentaire clinique structurée : hypothèses, examens, prise en charge et sources officielles (HAS, Collèges) citées au format Vancouver.",
  },
  '/medical-search': {
    title: `Recherche Ciblée — Réponses médicales rapides et sourcées | ${BASE}`,
    desc: "Posez une question médicale précise et obtenez une réponse courte, actionnable et sourcée : posologies, critères, seuils, recommandations françaises officielles.",
  },
  '/medications': {
    title: `Médicaments — DCI, spécialités et posologies (BDPM) | ${BASE}`,
    desc: "Recherchez un médicament par molécule ou nom commercial : DCI, spécialités françaises, posologies, contre-indications, grossesse (CRAT) et interactions.",
  },
  '/dermato': {
    title: `Dermatologie — Analyse documentaire photographique | ${BASE}`,
    desc: "Analyse documentaire de photographies dermatologiques avec description sémiologique, hypothèses documentaires et références bibliographiques officielles.",
  },
  '/cotation': {
    title: `Cotation CCAM & NGAP — Tarifs conventionnels officiels | ${BASE}`,
    desc: "Trouvez la bonne cotation CCAM (base officielle V83) et NGAP pour vos actes de médecine générale, avec tarifs conventionnels et règles d'association.",
  },
  '/scores': {
    title: `Scores Médicaux Validés — Calculateurs cliniques | ${BASE}`,
    desc: "Calculez les scores cliniques validés en médecine générale : CHA₂DS₂-VASc, HAS-BLED, Wells, CRB-65, FINDRISC, GAD-7, PHQ-9 et bien d'autres.",
  },
  '/biblio': {
    title: `Recherche Bibliographique — Articles et publications | ${BASE}`,
    desc: "Recherchez des références bibliographiques médicales au format Vancouver : articles, revues et publications pertinentes pour votre pratique.",
  },
  '/library': {
    title: `Ma Bibliothèque — Référentiels médicaux indexés par IA | ${BASE}`,
    desc: "Téléversez vos ouvrages médicaux de référence (PDF) : indexation automatique avec OCR, puis citation directe dans vos recherches documentaires.",
  },
  '/history': {
    title: `Historique — Vos recherches documentaires | ${BASE}`,
    desc: 'Retrouvez l’historique de vos recherches documentaires et synthèses cliniques réalisées avec Hippocrate MG.',
  },
  '/admin': {
    title: `Administration — ${BASE}`,
    desc: 'Espace d’administration Hippocrate MG : statistiques d’utilisation, gestion des utilisateurs et de la bibliothèque.',
  },
  '/mentions-legales': {
    title: `Mentions Légales | ${BASE}`,
    desc: 'Mentions légales de Hippocrate MG : éditeur, hébergement, propriété intellectuelle et conditions d’utilisation du service documentaire.',
  },
  '/confidentialite': {
    title: `Politique de Confidentialité (RGPD) | ${BASE}`,
    desc: 'Politique de confidentialité de Hippocrate MG : traitement des données, conformité RGPD, droits des utilisateurs et sécurité.',
  },
  '/reset-password': {
    title: `Réinitialisation du mot de passe | ${BASE}`,
    desc: 'Réinitialisez en toute sécurité le mot de passe de votre compte Hippocrate MG.',
  },
};

const setMeta = (selector, attr, value) => {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
};

export const SeoManager = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Le blog gère son propre SEO (BlogSeo) — ne pas écraser.
    if (pathname.startsWith('/blog')) return;
    const meta = META[pathname] || {
      title: `${BASE} — Assistant Documentaire IA du Médecin | CCAM, Scores, Recherche Médicale`,
      desc: DEFAULT_DESC,
    };
    document.title = meta.title;
    setMeta('meta[name="description"]', 'content', meta.desc);
    setMeta('meta[property="og:title"]', 'content', meta.title);
    setMeta('meta[property="og:description"]', 'content', meta.desc);
    setMeta('meta[property="og:url"]', 'content', `https://hippocrate.mg${pathname === '/' ? '/' : pathname}`);
    setMeta('meta[name="twitter:title"]', 'content', meta.title);
    setMeta('meta[name="twitter:description"]', 'content', meta.desc);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `https://hippocrate.mg${pathname === '/' ? '/' : pathname}`);
  }, [pathname]);

  return null;
};

export default SeoManager;
